import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createUserProfile } from '../firebase';
import { BsPlayFill, BsPauseFill } from 'react-icons/bs';
import { Instagram } from 'lucide-react';
import bgImage1 from '../assets/backgrounds/KidsWorkingOut.jpg';
import readyVideo from '../assets/backgrounds/generated-image-5de20b41-2cb3-4d92-85ad-d1d42a46e266.mp4';
import './LandingPage.css';
import { KiddiFitWordmark } from './Brand';
import AuthModal from './AuthModal';
import LandingHero from './landing/LandingHero';
import StateGuidesList from './StateGuidesList';
import useSampleWorkoutAudio from '../hooks/useSampleWorkoutAudio';

function LandingPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [carouselPosition, setCarouselPosition] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [infiniteAffirmations, setInfiniteAffirmations] = useState([]);

  // Scroll to top on component mount
  useEffect(() => {
    // Defer browser API calls until after hydration
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timer);
  }, []);
  const workoutSequence = [
    '/latest-generated-workout-audio/soccer_session_1_intro.mp3',
    '/latest-generated-exercises-audio/fastFeet_intro.mp3',
    '/latest-generated-exercises-audio/fastFeet.mp3',
    '/latest-generated-exercises-audio/highKnees_intro.mp3',
    '/latest-generated-exercises-audio/highKnees.mp3',
    '/latest-generated-exercises-audio/jumpingJacks_intro.mp3',
    '/latest-generated-exercises-audio/jumpingJacks.mp3',
    '/latest-generated-workout-audio/soccer_session_1_conclusion.mp3',
  ];
  const { isSamplePlaying, handleSampleWorkout } =
    useSampleWorkoutAudio(workoutSequence);

  const affirmations = [
    // Sports & Athletic Skills
    "You're a soccer star! ⚽",
    "You're a basketball champion! 🏀",
    "You're a swimming superstar! 🏊",
    "You're a dance master! 💃",
    "You're a martial arts warrior! 🥋",
    "You're a golf pro! ⛳",
    "You're a tennis ace! 🎾",
    "You're a baseball slugger! ⚾",
    "You're a volleyball champion! 🏐",
    "You're a track star! 🏃‍♂️",

    // Physical Attributes
    "You're lightning fast! ⚡",
    "You're incredibly strong! 💪",
    "You're a balance master! ⚖️",
    "You're a coordination wizard! 🎯",
    "You're an endurance champion! 🏃‍♂️",
    "You're a power machine! 🔥",
    "You're a flexibility expert! 🧘‍♀️",
    "You're super agile! 🦘",
    "You're amazingly quick! 🚀",
    "You're built for speed! 🏎️",

    // Mindset & Character
    "You're unstoppable! 🚀",
    "You're a true champion! 🏆",
    "You're getting stronger every day! 💪",
    "You're a fitness hero! 🦸‍♂️",
    "You're a workout warrior! ⚔️",
    "You're a motivation machine! 🔋",
    "You're a goal crusher! 🎯",
    "You're a progress maker! 📈",
    "You're a challenge conqueror! 🏔️",
    "You're a determination dynamo! 💎",

    // Skills & Abilities
    "You're a dribbling master! ⚽",
    "You're a shooting star! 🌟",
    "You're a passing pro! 🎯",
    "You're a defensive wall! 🛡️",
    "You're a team player! 🤝",
    "You're a strategy genius! 🧠",
    "You're a footwork wizard! 👟",
    "You're a hand-eye coordination expert! 👁️",
    "You're a reaction time champion! ⚡",
    "You're a spatial awareness master! 🎯",

    // Energy & Enthusiasm
    "You're full of energy! ⚡",
    "You're a positive force! ✨",
    "You're a joy to watch! 😊",
    "You're a natural leader! 👑",
    "You're a team motivator! 📢",
    "You're a sportsmanship role model! 🤝",
    "You're a never-give-up spirit! 💫",
    "You're a confidence builder! 💪",
    "You're a healthy lifestyle champion! 🥗",
    "You're a fitness inspiration! 🌟",

    // Growth & Progress
    "You're improving every workout! 📈",
    "You're building healthy habits! 🌱",
    "You're creating a strong foundation! 🏗️",
    "You're developing amazing skills! 🎨",
    "You're growing stronger mentally! 🧠",
    "You're building lasting friendships! 🤝",
    "You're learning valuable life lessons! 📚",
    "You're setting a great example! 👑",
    "You're making your family proud! ❤️",
    "You're becoming the best version of yourself! 🌟",
  ];

  const features = [
    {
      icon: '🎯',
      title: 'Voice-Guided Workouts',
      description:
        'Audio-led workouts guide kids through fun, full-body exercises like jumping jacks and squats. All motivation, no screens, no stress. Kids become the beasts they were born to be.',
    },
    {
      icon: '⚽',
      title: 'Sport-Specific Skills',
      description:
        'Audio-led drills for sports like soccer, baseball, basketball, dance, and karate. Kids master fundamentals, advanced concepts, and become the masters of their sport.',
    },
    {
      icon: '🏆',
      title: 'Points, Badges & Levels',
      description:
        'Workouts are gamified! Kids earn points, unlock new levels, and collect badges for streaks, effort, and skill-building. Every session feels like a win.',
    },
    {
      icon: '💰',
      title: 'Points Bank with Custom Rewards',
      description:
        'Every completed workout earns points. Points go into a bank you control. Use your secure PIN (like an ATM) to redeem points for real-world rewards that you decide together.',
    },
    {
      icon: '👥',
      title: 'Multiple Players + Custom Avatars',
      description:
        'Track progress for each child with personalized avatars and profiles. Perfect for siblings and friends—everyone stays engaged and on their own journey.',
    },
    {
      icon: '📊',
      title: 'Progress You Can See',
      description:
        'Monitor streaks, levels, and achievements. Watch your kids grow stronger, more confident, and more capable—one 10-minute session at a time.',
    },
  ];

  // Initialize the infinite list
  useEffect(() => {
    setInfiniteAffirmations([
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
      ...affirmations,
    ]);
  }, []);

  // Move carousel upward every 4 seconds with dynamic list management
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselPosition((prev) => {
        const newPosition = prev + 40;

        // Update active index - the item in the center of the viewport (60px from top)
        const newActiveIndex =
          Math.floor((newPosition + 60) / 40) % affirmations.length;
        setActiveIndex(newActiveIndex);

        // Check if we're approaching the end of the current list
        const itemsVisible = Math.floor(newPosition / 40) + 3; // 3 items visible at once
        const totalItems = infiniteAffirmations.length;

        // If we're within 2 sets of the end, add more items
        if (itemsVisible >= totalItems - affirmations.length * 2) {
          setInfiniteAffirmations((prev) => {
            const newList = [...prev, ...affirmations];
            // Remove old items from the beginning to keep memory usage reasonable
            if (newList.length > affirmations.length * 5) {
              return newList.slice(affirmations.length);
            }
            return newList;
          });
        }

        return newPosition;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [affirmations.length, infiniteAffirmations.length]);

  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleLogin = async (user) => {
    try {
      await createUserProfile(user.uid, {
        displayName: user.displayName || user.email?.split('@')[0],
        email: user.email,
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Profile creation error:', error);
      // Still navigate to dashboard even if profile creation fails
      navigate('/dashboard');
    }
  };

  const handleShowAuthModal = () => {
    setShowAuthModal(true);
  };

  // scroll handled inside LandingHero now

  // sample workout behavior provided by useSampleWorkoutAudio hook

  return (
    <div className='landing-page'>
      {/* Hero Section (componentized) */}
      <LandingHero />

      {/* Features Section */}
      <section className='features-section'>
        <div className='container'>
          <h2>
            Why Kids Love <KiddiFitWordmark />
          </h2>
          <div className='features-grid'>
            {features.map((feature, index) => (
              <div key={index} className='feature-card'>
                <div className='feature-icon'>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Workout Section */}
      <section className='sample-workout-section sample-workout-section-with-bg'>
        <div className='container'>
          <div className='sample-workout-panel'>
            <div className='sample-header'>
              <div className='sample-icon'>✨</div>
              <h3>Experience the Magic</h3>
              <p className='sample-subtitle'>
                Our upbeat voice coaches bring every session to life with:
              </p>
            </div>

            <div className='sample-description'>
              <div className='coaching-features'>
                <div className='coaching-feature'>
                  <div className='coaching-icon'>✨</div>
                  <div className='coaching-text'>
                    <h4>Positive Affirmations</h4>
                    <p>Building confidence with every word</p>
                  </div>
                </div>
                <div className='coaching-feature'>
                  <div className='coaching-icon'>🚀</div>
                  <div className='coaching-text'>
                    <h4>Kid-Friendly Motivation</h4>
                    <p>Fun encouragement that keeps them moving</p>
                  </div>
                </div>
                <div className='coaching-feature'>
                  <div className='coaching-icon'>🛡️</div>
                  <div className='coaching-text'>
                    <h4>Clear, Safe Instructions</h4>
                    <p>Easy-to-follow guidance for proper form</p>
                  </div>
                </div>
                <div className='coaching-feature'>
                  <div className='coaching-icon'>🎯</div>
                  <div className='coaching-text'>
                    <h4>Perfect for Kids</h4>
                    <p>
                      Voice-guided, 10-minute sessions with no equipment needed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              className='sample-workout-btn'
              onClick={(e) => {
                console.log('🎵 Panel Sample Workout button clicked!');
                e.stopPropagation();
                handleSampleWorkout();
              }}
            >
              {isSamplePlaying ? <BsPauseFill /> : <BsPlayFill />}
              {isSamplePlaying ? 'Stop Sample' : 'Sample Workout'}
            </button>
          </div>
        </div>
      </section>

      {/* Ready to Get Started Section */}
      <section className='ready-section ready-section-fixed-height'>
        <div className='container'>
          <video
            className='ready-video ready-video-wide'
            autoPlay
            muted
            loop
            playsInline
            poster={bgImage1}
          >
            <source src={readyVideo} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
          <div className='ready-content'>
            <h2>Ready to Get Started?</h2>
            <p className='ready-description'>
              Give your kids the tools to grow strong, confident, and
              sport-ready—right from home.
            </p>
            <div className='hero-buttons'>
              <button
                className='sample-workout-btn'
                onClick={(e) => {
                  console.log(
                    '🎵 Ready Section Sample Workout button clicked!'
                  );
                  e.stopPropagation();
                  handleSampleWorkout();
                }}
              >
                {isSamplePlaying ? <BsPauseFill /> : <BsPlayFill />}
                {isSamplePlaying ? 'Sample Workout' : 'Sample Workout'}
              </button>

              <button
                className='google-login-btn'
                onClick={handleShowAuthModal}
                disabled={loading}
              >
                {/* <svg className='google-icon' viewBox='0 0 24 24'>
                  <path
                    fill='#4285F4'
                    d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                  />
                  <path
                    fill='#34A853'
                    d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                  />
                  <path
                    fill='#FBBC05'
                    d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                  />
                  <path
                    fill='#EA4335'
                    d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                  />
                </svg> */}
                {loading ? (
                  'Signing in...'
                ) : (
                  <>
                    <span>👋</span> Join or Login
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className='testimonials-section'>
        <div className='container'>
          <h2>What Parents Are Saying</h2>
          <div className='testimonials-grid'>
            <div className='testimonial-card'>
              <div className='testimonial-content'>
                <p>
                  My kids actually ask to do workouts now! The voice coaching is
                  amazing and they love earning points. It's become our favorite
                  family activity.
                </p>
              </div>
              <div className='testimonial-author'>
                <img
                  src='/assets/images/Sarah.jpg'
                  alt='Sarah M.'
                  className='author-image'
                />
                <div className='author-info'>
                  <h4>Sarah M.</h4>
                  <span>Mom of 2</span>
                </div>
              </div>
            </div>

            <div className='testimonial-card'>
              <div className='testimonial-content'>
                <p>
                  Finally, a fitness app that keeps my kids engaged! The
                  10-minute sessions are perfect and they're building real
                  confidence. Highly recommend!
                </p>
              </div>
              <div className='testimonial-author'>
                <img
                  src='/assets/images/Mike.jpg'
                  alt='Mike R.'
                  className='author-image'
                />
                <div className='author-info'>
                  <h4>Mike R.</h4>
                  <span>Dad of 3</span>
                </div>
              </div>
            </div>

            <div className='testimonial-card'>
              <div className='testimonial-content'>
                <p>
                  The points system is genius! My daughter loves working toward
                  rewards and I love that she's staying active. Win-win!
                </p>
              </div>
              <div className='testimonial-author'>
                <img
                  src='/assets/images/Jennifer.jpg'
                  alt='Jennifer L.'
                  className='author-image'
                />
                <div className='author-info'>
                  <h4>Jennifer L.</h4>
                  <span>Mom of 1</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affirmations Section */}
      <section className='affirmations-section'>
        <div className='container'>
          <h2>Building Confidence Through Affirmations</h2>
          <div className='affirmations-carousel'>
            <div className='carousel-viewport'>
              <div
                className='carousel-container carousel-container-dynamic'
                style={{
                  transform: `translateY(-${carouselPosition}px)`,
                }}
              >
                {infiniteAffirmations.map((affirmation, index) => {
                  // Calculate which item this is in the original affirmations array
                  const originalIndex = index % affirmations.length;
                  const isActive = originalIndex === activeIndex;

                  return (
                    <div
                      key={`${affirmation}-${index}`}
                      className={`carousel-item ${isActive ? 'active' : ''}`}
                    >
                      {affirmation}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='landing-footer'>
        <div className='container'>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
            }}
          >
            <p>
              &copy; 2025 HHG Labs,{' '}
              <a href='/' className='footer-link-clean'>
                <KiddiFitWordmark />
              </a>
            </p>
            <a
              href='https://www.instagram.com/kiddi_fit'
              target='_blank'
              rel='noopener noreferrer'
              className='footer-link-clean'
            >
              <Instagram className='footer-icon' />
              <span> DM us at @kiddi_fit</span>
            </a>
          </div>
        </div>
      </footer>

      {/* State Guides Section */}
      <StateGuidesList />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleLogin}
        initialMode='login'
      />
    </div>
  );
}

export default LandingPage;
